<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';

  import { ImageBulkUploadForm } from '@client/pages/manage/assets/modals/shared';

  import { AssetsService } from '@client/services/assets';

  import { appStore } from '@client/stores/app';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const MAX_FILE_SIZE = 3 * 1024 * 1024;

  let files = [];
  let inline = false;
  let uploadSuccess = false;
  let uploadedFilenames = [];

  const onFileChange = () => {
    uploadSuccess = false;
  };

  const onRemoveFile = (index) => {
    files = files.filter((_, i) => i !== index);
  };

  const executeCreateImages = async () => {
    $appStore.loading = true;
    uploadedFilenames = [];

    try {
      for (const bulkFile of files) {
        const file = new FormData();
        file.append('file', bulkFile);

        const response = await AssetsService.bulkUploadFiles(file);

        if (response?.status >= 400) {
          notify($format('label.FAILURE.ASSET_UPLOAD', { values: { asset: bulkFile.name } }), 'error');
          throw new Error(`Failed to upload file: ${bulkFile.name}`);
        }

        const { hash, filesize, filename, metadata } = response;

        if (inline) {
          let image = await AssetsService.getByHash(filename, 'images');

          image = { ...image, inline: true };

          const inlineUpdate = await AssetsService.updateImage(image);

          if (inlineUpdate?.status >= 400) {
            notify($format('label.FAILURE.ASSET_UPLOAD', { values: { asset: filename } }), 'error');
            throw new Error(`Failed to update image: ${filename}`);
          }
        }

        uploadedFilenames.push(filename);
        notify($format('label.SUCCESS.ASSET_UPLOAD', { values: { asset: filename } }));
      }

      $appStore.loading = false;
      uploadSuccess = true;

      step.data = {
        ids: uploadedFilenames
      };

      return { success: true };
    } catch (error) {
      $appStore.loading = false;
      throw error;
    }
  };

  const rollbackCreateImages = async () => {
    if (!step.data?.ids || step.data.ids.length === 0) {
      return;
    }

    $appStore.loading = true;

    try {
      const images = await AssetsService.getAllImages();

      for (const id of step.data.ids) {
        const image = images.find((img) => img.filename === id);

        if (image) {
          await AssetsService.deleteImage(image);
        }
      }
    } catch (error) {
      console.error('Failed to rollback image creation:', error);
    } finally {
      $appStore.loading = false;
    }
  };

  onMount(() => {
    if (onExecute) {
      onExecute(executeCreateImages);
    }
    if (onRollback) {
      onRollback(rollbackCreateImages);
    }
  });

  $: nextDisabled = !files.length;
</script>

<div class="image-bulk-create-form">
  <ImageBulkUploadForm
    bind:files
    bind:inline
    bind:uploadSuccess
    maxFileSize={MAX_FILE_SIZE}
    {onFileChange}
    {onRemoveFile}
    inputId="workflow-image-bulk-create"
  />
</div>

<style lang="scss">
  .image-bulk-create-form {
    width: 100%;
  }
</style>
